import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, Clock, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useConfig } from '../../context/ConfigContext';
import useModalBack from '../../hooks/useModalBack';
import { isIOS } from '../../services/native';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const FULL_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export default function DateTimeInput({
  value, // { date: 'YYYY-MM-DD', time: 'HH:mm' } or just date string for dateOnly mode, or 'HH:mm' for timeOnly
  onChange, // Called with { date: 'YYYY-MM-DD', time: 'HH:mm' } or just date for dateOnly, or 'HH:mm' for timeOnly
  name,
  dateOnly = false,
  timeOnly = false,
  error = false,
  placeholder = 'Select date & time',
  className = '',
  minDate = '', // 'YYYY-MM-DD'
  maxDate = ''  // 'YYYY-MM-DD'
}) {
  const { config, formatTime } = useConfig();
  const is12Hour = config.timeFormat === '12h';

  const nativeDateRef = useRef(null);
  const nativeTimeRef = useRef(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const closeDatePicker = useCallback(() => setShowDatePicker(false), []);
  const closeTimePicker = useCallback(() => setShowTimePicker(false), []);
  useModalBack(showDatePicker, closeDatePicker, 'DatePicker');
  useModalBack(showTimePicker, closeTimePicker, 'TimePicker');

  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('12:00');
  const [showYearPicker, setShowYearPicker] = useState(false);

  // Parse value on mount and when value changes (only when modals are closed)
  useEffect(() => {
    if (showDatePicker || showTimePicker) return;

    if (timeOnly) {
      if (typeof value === 'string' && value) {
        setSelectedTime(value);
      }
      return;
    }

    if (typeof value === 'object' && value !== null) {
      if (value.date) {
        setSelectedDate(value.date);
        const [year, month] = value.date.split('-').map(Number);
        if (year && month) {
          setCurrentMonth(month - 1);
          setCurrentYear(year);
        }
      }
      if (value.time) {
        setSelectedTime(value.time);
      }
    } else if (typeof value === 'string' && value) {
      setSelectedDate(value);
      const [year, month] = value.split('-').map(Number);
      if (year && month) {
        setCurrentMonth(month - 1);
        setCurrentYear(year);
      }
    } else if (value === '' || value === null || value === undefined) {
      setSelectedDate('');
    }
  }, [value, showDatePicker, showTimePicker, timeOnly]);

  // Get days in month
  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
    const daysInPrevMonth = getDaysInMonth(currentYear, currentMonth - 1);
    const days = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      const day = daysInPrevMonth - i;
      const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
      const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
      days.push({
        day, month: prevMonth, year: prevYear, isCurrentMonth: false,
        date: `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        day, month: currentMonth, year: currentYear, isCurrentMonth: true,
        date: `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      });
    }

    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
      const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
      days.push({
        day, month: nextMonth, year: nextYear, isCurrentMonth: false,
        date: `${nextYear}-${String(nextMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      });
    }

    return days;
  }, [currentMonth, currentYear]);

  const isToday = (date) => {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    return date === todayStr;
  };

  const isDateDisabled = (date) => {
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };

  const goToPreviousMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(currentYear - 1); }
    else setCurrentMonth(currentMonth - 1);
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(currentYear + 1); }
    else setCurrentMonth(currentMonth + 1);
  };

  const handleDateSelect = (dayInfo) => {
    setSelectedDate(dayInfo.date);
    setCurrentMonth(dayInfo.month);
    setCurrentYear(dayInfo.year);
  };

  // Time helpers
  const parseTime = (timeStr) => {
    if (!timeStr) return { hours: 12, minutes: 0 };
    const [h, m] = timeStr.split(':').map(Number);
    return { hours: h || 0, minutes: m || 0 };
  };

  const { hours, minutes } = parseTime(selectedTime);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = is12Hour ? (hours % 12 || 12) : hours;

  const updateTime = (newHours, newMinutes, newPeriod = period) => {
    let h = newHours;
    if (is12Hour) {
      h = newPeriod === 'AM' ? (newHours === 12 ? 0 : newHours) : (newHours === 12 ? 12 : newHours + 12);
    }
    const timeStr = `${String(h).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
    setSelectedTime(timeStr);
  };

  // Confirm handlers
  const handleDateConfirm = () => {
    if (!selectedDate) return;
    if (dateOnly) {
      onChange(selectedDate, name);
    } else {
      onChange({ date: selectedDate, time: selectedTime }, name);
    }
    setShowDatePicker(false);
  };

  const handleTimeConfirm = () => {
    if (timeOnly) {
      onChange(selectedTime, name);
    } else {
      onChange({ date: selectedDate, time: selectedTime }, name);
    }
    setShowTimePicker(false);
    setEditingMinute(false);
  };

  const setToday = () => {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    setSelectedDate(todayStr);
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
  };

  const setNow = () => {
    const now = new Date();
    const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    setSelectedTime(timeStr);
  };

  // Format display values
  const formatDisplayDate = () => {
    const dateStr = selectedDate || (typeof value === 'string' ? value : value?.date);
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-').map(Number);
    return `${String(day).padStart(2, '0')} ${MONTHS[month - 1]} ${year}`;
  };

  const formatDisplayTime = () => {
    const timeStr = selectedTime || value?.time || '12:00';
    return formatTime(timeStr);
  };

  // Generate year options
  const yearOptions = useMemo(() => {
    const currentYearNow = new Date().getFullYear();
    const years = [];
    for (let y = currentYearNow - 10; y <= currentYearNow + 5; y++) years.push(y);
    return years;
  }, []);

  // Generate hour/minute options for scroll picker
  const hourOptions = useMemo(() => {
    if (is12Hour) return Array.from({ length: 12 }, (_, i) => i + 1);
    return Array.from({ length: 24 }, (_, i) => i);
  }, [is12Hour]);

  const minuteOptions = useMemo(() => Array.from({ length: 12 }, (_, i) => i * 5), []);
  const [editingMinute, setEditingMinute] = useState(false);
  const minuteInputRef = useRef(null);

  // Quick time presets
  const timePresets = [
    { label: 'Now', action: setNow },
    { label: '9:00 AM', time: '09:00' },
    { label: '12:00 PM', time: '12:00' },
    { label: '6:00 PM', time: '18:00' },
  ];

  return (
    <div className="relative">
      {/* Trigger fields */}
      <div className={`grid ${(dateOnly || timeOnly) ? 'grid-cols-1' : 'grid-cols-2'} gap-2`}>
        {/* Date field */}
        {!timeOnly && (
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                if (!isIOS) setShowDatePicker(true);
                // On iOS, the transparent native input overlay handles the tap
              }}
              className={`flex items-center gap-2 px-3 py-2.5 border rounded-xl bg-white transition-colors text-left w-full ${
                error ? 'border-red-500' : 'border-surface-200 hover:border-surface-300'
              } ${className}`}
            >
              <Calendar className="w-4 h-4 text-surface-400 flex-shrink-0" />
              <span className={`text-sm truncate ${selectedDate ? 'text-surface-900' : 'text-surface-400'}`}>
                {formatDisplayDate() || placeholder || 'Select date'}
              </span>
            </button>
            {/* Hidden native date input for iOS */}
            {isIOS && (
              <input
                ref={nativeDateRef}
                type="date"
                value={selectedDate}
                onChange={(e) => {
                  const newDate = e.target.value;
                  if (newDate) {
                    setSelectedDate(newDate);
                    const [y, m] = newDate.split('-').map(Number);
                    setCurrentMonth(m - 1);
                    setCurrentYear(y);
                    if (dateOnly) onChange(newDate, name);
                    else onChange({ date: newDate, time: selectedTime }, name);
                  }
                }}
                className="absolute inset-0 opacity-0 w-full h-full"
                tabIndex={-1}
              />
            )}
          </div>
        )}

        {/* Time field */}
        {!dateOnly && (
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                if (!isIOS) setShowTimePicker(true);
              }}
              className={`flex items-center gap-2 px-3 py-2.5 border rounded-xl bg-white transition-colors text-left w-full ${
                error ? 'border-red-500' : 'border-surface-200 hover:border-surface-300'
              } ${className}`}
            >
              <Clock className="w-4 h-4 text-surface-400 flex-shrink-0" />
              <span className="text-sm text-surface-900 truncate">
                {formatDisplayTime()}
              </span>
            </button>
            {/* Hidden native time input for iOS */}
            {isIOS && (
              <input
                ref={nativeTimeRef}
                type="time"
                value={selectedTime}
                onChange={(e) => {
                  const newTime = e.target.value;
                  if (newTime) {
                    setSelectedTime(newTime);
                    if (timeOnly) onChange(newTime, name);
                    else onChange({ date: selectedDate, time: newTime }, name);
                  }
                }}
                className="absolute inset-0 opacity-0 w-full h-full"
                tabIndex={-1}
              />
            )}
          </div>
        )}
      </div>

      {/* Date Picker Bottom Sheet */}
      {showDatePicker && createPortal(
        <div
          className="fixed inset-0 bg-surface-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-[100]"
          onClick={() => setShowDatePicker(false)}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm max-h-[85vh] flex flex-col animate-slide-up sm:mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 rounded-full bg-surface-300" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-surface-100">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-surface-500" />
                <h3 className="text-lg font-semibold text-surface-900">Select Date</h3>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={setToday}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-surface-200 text-surface-700 hover:bg-surface-50 transition-colors"
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={() => setShowDatePicker(false)}
                  className="p-1.5 text-surface-400 hover:text-surface-600 rounded-lg transition-colors hover:bg-surface-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-4 py-3">
              {/* Month/Year Navigation */}
              <div className="flex items-center justify-between mb-3">
                <button
                  type="button"
                  onClick={goToPreviousMonth}
                  className="p-2 rounded-xl hover:bg-surface-100 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-surface-600" />
                </button>

                <button
                  type="button"
                  onClick={() => setShowYearPicker(!showYearPicker)}
                  className="px-4 py-1.5 rounded-xl hover:bg-surface-100 transition-colors"
                >
                  <span className="font-bold text-base text-surface-900">
                    {FULL_MONTHS[currentMonth]} {currentYear}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={goToNextMonth}
                  className="p-2 rounded-xl hover:bg-surface-100 transition-colors"
                >
                  <ChevronRight className="w-5 h-5 text-surface-600" />
                </button>
              </div>

              {/* Year Picker */}
              {showYearPicker && (
                <div className="mb-3 p-2 bg-surface-50 rounded-xl max-h-32 overflow-y-auto">
                  <div className="grid grid-cols-4 gap-1">
                    {yearOptions.map((year) => (
                      <button
                        key={year}
                        type="button"
                        onClick={() => { setCurrentYear(year); setShowYearPicker(false); }}
                        className={`py-2 rounded-lg text-sm font-medium transition-all ${
                          year === currentYear
                            ? 'text-white'
                            : 'text-surface-600 hover:bg-surface-100'
                        }`}
                        style={year === currentYear ? { backgroundColor: 'var(--accent-bg)' } : {}}
                      >
                        {year}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-1 mb-1">
                {DAYS.map((day, i) => (
                  <div key={i} className="text-center text-xs font-semibold text-surface-400 py-1.5">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((dayInfo, index) => {
                  const isSelected = dayInfo.date === selectedDate;
                  const isTodayDate = isToday(dayInfo.date);
                  const disabled = isDateDisabled(dayInfo.date);

                  return (
                    <button
                      key={index}
                      type="button"
                      disabled={disabled}
                      onClick={() => !disabled && handleDateSelect(dayInfo)}
                      className={`aspect-square flex items-center justify-center rounded-xl text-sm font-medium transition-all ${
                        disabled
                          ? 'text-surface-200 cursor-not-allowed opacity-30 line-through'
                          : isSelected
                          ? 'text-white shadow-sm'
                          : isTodayDate
                          ? 'ring-2 ring-offset-1 text-surface-900 hover:bg-surface-100 font-bold'
                          : dayInfo.isCurrentMonth
                          ? 'text-surface-800 hover:bg-surface-100'
                          : 'text-surface-300'
                      }`}
                      style={{
                        backgroundColor: isSelected && !disabled ? 'var(--accent-bg)' : undefined,
                        '--tw-ring-color': isTodayDate && !isSelected ? 'var(--accent-bg)' : undefined
                      }}
                    >
                      {dayInfo.day}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-surface-100">
              <button
                type="button"
                onClick={handleDateConfirm}
                disabled={!selectedDate}
                className="w-full py-3 rounded-xl font-semibold text-sm text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: 'var(--accent-bg)' }}
              >
                Done
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Time Picker Bottom Sheet */}
      {showTimePicker && createPortal(
        <div
          className="fixed inset-0 bg-surface-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-[100]"
          onClick={() => setShowTimePicker(false)}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm flex flex-col animate-slide-up sm:mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 rounded-full bg-surface-300" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-surface-100">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-surface-500" />
                <h3 className="text-lg font-semibold text-surface-900">Select Time</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowTimePicker(false)}
                className="p-1.5 text-surface-400 hover:text-surface-600 rounded-lg transition-colors hover:bg-surface-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Time Display */}
            <div className="px-4 pt-4 pb-2">
              <div className="flex items-center justify-center gap-1 text-4xl font-bold text-surface-900 tracking-wider">
                <span>{String(displayHours).padStart(2, '0')}</span>
                <span className="text-surface-300">:</span>
                {editingMinute ? (
                  <input
                    ref={minuteInputRef}
                    type="tel"
                    defaultValue={String(minutes).padStart(2, '0')}
                    maxLength={2}
                    className="w-16 text-center text-4xl font-bold bg-surface-50 rounded-xl border-2 outline-none tabular-nums"
                    style={{ borderColor: 'var(--accent-bg)' }}
                    onBlur={(e) => {
                      let val = parseInt(e.target.value) || 0;
                      if (val > 59) val = 59;
                      if (val < 0) val = 0;
                      if (is12Hour) updateTime(displayHours, val, period);
                      else updateTime(hours, val);
                      setEditingMinute(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') e.target.blur();
                    }}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingMinute(true);
                      setTimeout(() => minuteInputRef.current?.select(), 50);
                    }}
                    className="px-2 py-0.5 rounded-xl hover:bg-surface-100 transition-colors"
                  >
                    {String(minutes).padStart(2, '0')}
                  </button>
                )}
                {is12Hour && <span className="text-lg font-semibold text-surface-500 ml-2">{period}</span>}
              </div>
              {!editingMinute && (
                <p className="text-center text-[11px] text-surface-400 mt-1">Tap minutes to type exact value</p>
              )}
            </div>

            {/* Hour selection */}
            <div className="px-4 py-3">
              <p className="text-xs font-semibold text-surface-400 uppercase tracking-wider mb-2">Hour</p>
              <div className={`grid ${is12Hour ? 'grid-cols-6' : 'grid-cols-8'} gap-1.5`}>
                {hourOptions.map((h) => {
                  const isActive = h === displayHours;
                  return (
                    <button
                      key={h}
                      type="button"
                      onClick={() => updateTime(h, minutes, period)}
                      className={`py-2 rounded-xl text-sm font-semibold transition-all ${
                        isActive
                          ? 'text-white shadow-sm'
                          : 'text-surface-700 bg-surface-50 hover:bg-surface-100'
                      }`}
                      style={isActive ? { backgroundColor: 'var(--accent-bg)' } : {}}
                    >
                      {is12Hour ? h : String(h).padStart(2, '0')}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Minute selection */}
            <div className="px-4 py-2">
              <p className="text-xs font-semibold text-surface-400 uppercase tracking-wider mb-2">Minute</p>
              <div className="grid grid-cols-6 gap-1.5">
                {minuteOptions.map((m) => {
                  const isActive = m === minutes;
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => {
                        if (is12Hour) updateTime(displayHours, m, period);
                        else updateTime(hours, m);
                      }}
                      className={`py-2 rounded-xl text-sm font-semibold transition-all ${
                        isActive
                          ? 'text-white shadow-sm'
                          : 'text-surface-700 bg-surface-50 hover:bg-surface-100'
                      }`}
                      style={isActive ? { backgroundColor: 'var(--accent-bg)' } : {}}
                    >
                      :{String(m).padStart(2, '0')}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* AM/PM toggle */}
            {is12Hour && (
              <div className="px-4 py-2">
                <div className="flex gap-2">
                  {['AM', 'PM'].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => updateTime(displayHours, minutes, p)}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${
                        period === p
                          ? 'text-white shadow-sm'
                          : 'text-surface-700 bg-surface-50 hover:bg-surface-100'
                      }`}
                      style={period === p ? { backgroundColor: 'var(--accent-bg)' } : {}}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quick presets */}
            <div className="px-4 py-2">
              <div className="flex gap-2">
                {timePresets.map((preset, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      if (preset.action) preset.action();
                      else if (preset.time) setSelectedTime(preset.time);
                    }}
                    className="flex-1 py-2 rounded-xl border border-surface-200 text-xs font-semibold text-surface-600 hover:bg-surface-50 transition-colors"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-surface-100 mt-1">
              <button
                type="button"
                onClick={handleTimeConfirm}
                className="w-full py-3 rounded-xl font-semibold text-sm text-white transition-colors flex items-center justify-center gap-2"
                style={{ backgroundColor: 'var(--accent-bg)' }}
              >
                Done
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
